import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, money, unitPrice, quantity, date, PAYMENT_LABEL } from '../api.js';
import { amountInWords } from '../numberWords.js';
import { dobaviDni, vatRateOf, opisanieNaReda, slivaniRedove } from '../printLines.js';
import { useMailSend } from '../components/MailSend.jsx';

/**
 * Печатен изглед на обединена фактура — една фактура, покриваща няколко
 * продажби (приемо-предавателни протоколи) на един клиент през месеца.
 *
 * Устройството на страницата умишлено следва PrintDocument.jsx (общото
 * лого/шапка/подпис), но е самостоятелен компонент, не режим на него:
 * тук няма „Редакция" на описанието по ред (редовете идват от няколко
 * различни документа, а ръчната поправка е нещо, което засяга един) и
 * няма превключване фактура/проформа/ППП — само фактура, само една.
 */
export default function PrintInvoice() {
  const { invoiceNo } = useParams();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/api/invoices/${invoiceNo}`), api.get('/api/settings')])
      .then(([d, s]) => { setInv(d); setCompany(s.company ?? {}); })
      .catch((e) => setError(e.message));
  }, [invoiceNo]);

  // „Мейл“ (30.10.2026) — същото писмо като при един документ, с PDF-а на цялата фактура.
  const mail = useMailSend({
    endpoint: `/api/invoices/${encodeURIComponent(invoiceNo)}/send-mail`,
    email: inv?.partnerEmail,
    partnerId: inv?.partnerId,
    partnerName: inv?.partnerName,
    viewHref: `/api/invoices/${encodeURIComponent(invoiceNo)}/pdf`,
    onEmailSaved: (value) => setInv((d) => ({ ...d, partnerEmail: value })),
  });

  if (error) return <div className="error" style={{ margin: 20 }}>{error}</div>;
  if (!inv) return <div className="empty">Зареждане…</div>;

  // Виж бележката в PrintDocument.jsx — анулираните и подарените (0.00)
  // редове не излизат на фактурата, а по партиди на една и съща стока се
  // сливат, вкл. през границата на отделните протоколи.
  const printItems = slivaniRedove(
    inv.items.filter((it) => !Number(it.is_cancelled) && Number(it.line_subtotal) !== 0)
  );

  return (
    <>
      <div className="print-toolbar">
        <button className="primary" onClick={() => window.print()}>Печат</button>
        {mail.button}
        <a className="button" href={`/api/invoices/${encodeURIComponent(invoiceNo)}/pdf`} download>PDF</a>
        <div style={{ flex: 1 }} />
        <button onClick={() => window.close()}>Затвори</button>
      </div>

      {mail.notice}
      {mail.modals}

      <div className="print-page s-baner">
        <div className="print-letterhead">
          <div>
            <img src="/brand/dp-logo.jpg" alt="Доктор Принтер" className="print-logo" />
            <div className="print-original">ОРИГИНАЛ</div>
          </div>
          <div className="print-contacts">
            <div>гр. Варна, ул. Батак 7</div>
            <div>тел. 052 989 334, 052 310 520, 0878 727 397</div>
            <div>Teams: doctor_printer</div>
            <div>office@doctorprinter.bg · www.doctorprinter.bg</div>
          </div>
        </div>

        <div className="print-title">
          <h1>ФАКТУРА</h1>
          <div className="sub">
            № {inv.invoiceNo} / {date(inv.invoiceDate)}
          </div>
        </div>

        <div className="print-head">
          <div className="party">
            <h2>Получател</h2>
            <div className="name">{inv.partnerName ?? 'Физическо лице'}</div>
            <Row k="ЕИК" v={inv.partnerEik} />
            <Row k="ДДС №" v={inv.partnerVat} />
            <Row k="Адрес" v={[inv.partnerCity, inv.partnerAddress].filter(Boolean).join(', ')} />
            <Row k="МОЛ" v={inv.partnerMol} />
          </div>

          <div className="party">
            <h2>Доставчик</h2>
            <div className="name">{company.name}</div>
            <Row k="ЕИК" v={company.eik} />
            <Row k="ДДС №" v={company.vat_number} />
            <Row k="Адрес" v={[company.city, company.address].filter(Boolean).join(', ')} />
            <Row k="МОЛ" v={company.mol} />
          </div>
        </div>

        <table className="print-lines">
          <thead>
            <tr>
              <th style={{ width: '8mm' }}>№</th>
              <th>Наименование</th>
              <th style={{ width: '16mm' }}>Мярка</th>
              <th className="num" style={{ width: '20mm' }}>Кол.</th>
              <th className="num" style={{ width: '26mm' }}>Ед. цена</th>
              <th className="num" style={{ width: '28mm' }}>Стойност</th>
            </tr>
          </thead>
          <tbody>
            {printItems.map((it, i) => (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>
                  {opisanieNaReda(it)}
                  {it.note && <span className="line-note"> — {it.note}</span>}
                </td>
                <td>{it.unit}</td>
                <td className="num">{quantity(it.quantity)}</td>
                <td className="num">{unitPrice(it, inv.currency)}</td>
                <td className="num">{money(it.line_subtotal, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-totals">
          <div><span>Данъчна основа</span><span>{money(inv.subtotal, inv.currency)}</span></div>
          <div><span>ДДС {vatRateOf(inv.items)}%</span><span>{money(inv.vatTotal, inv.currency)}</span></div>
          <div className="grand"><span>Общо за плащане</span><span>{money(inv.total, inv.currency)}</span></div>
        </div>

        <div className="in-words">
          <span className="k">Словом: </span>
          {amountInWords(inv.total, inv.currency)}
        </div>

        {/* Редовете по-горе са слети по стока — кои точно протоколи влизат
            във фактурата се вижда тук, за проследимост. */}
        {inv.documents.length > 1 && (
          <div className="print-meta">
            <div>
              <span className="k">По протоколи № </span>
              {inv.documents.map((d) => `${d.docNumber} (${date(d.docDate)})`).join(', ')}
            </div>
          </div>
        )}

        <div className="print-meta">
          <div><span className="k">Начин на плащане: </span>{PAYMENT_LABEL[inv.paymentMethod]}</div>
          {inv.paymentMethod === 'bank' && (
            <div>
              <span className="k">Срок на плащане до: </span>
              {date(inv.dueDate ?? dobaviDni(inv.invoiceDate, inv.partnerCreditDays))}
            </div>
          )}
        </div>

        {inv.paymentMethod === 'bank' && company.bank_iban && (
          <div className="print-meta">
            <div><span className="k">Банкова сметка: </span>{company.bank_iban}</div>
          </div>
        )}

        <div className="signatures">
          <div>
            <div className="line" />
            Получил
          </div>
          <div>
            <div className="line" />
            Съставил: {inv.createdByName ?? ''}
          </div>
        </div>

        <div className="print-foot">
          Документът е съставен от Тефтер — складова система, отговаряща на Приложение № 29 към Наредба Н-18.
        </div>

        <div className="print-banner">
          <img src="/brand/paper-banner.jpg" alt="Хартия А4 80гр — доставка" />
        </div>
      </div>
    </>
  );
}

function Row({ k, v }) {
  if (!v) return null;
  return <div className="row"><span className="k">{k}:</span><span>{v}</span></div>;
}
